import type { components } from "@colossus/api-client";
import {
  FREE_SKU,
  TIER_PRO_SKU,
  TITAN_L_SKU,
  TITAN_M_SKU,
  TITAN_S_SKU,
  hourlyDays,
} from "@colossus/contracts";
import type { Entitlement, EntitlementStatus } from "@colossus/contracts";
import { m } from "./paraglide/messages.js";
import type { Message } from "./locale.ts";

type Schemas = components["schemas"];

export type PlanSku = Schemas["PlanSkuView"];

export type TenantPlan = Schemas["TenantPlanView"];

const KIND_RANK: Readonly<Record<PlanSku["kind"], number>> = { tier: 0, module: 1, addon: 2 };

export const orderedSkus = (plan: TenantPlan): readonly PlanSku[] =>
  [...plan.skus].sort(
    (left, right) =>
      KIND_RANK[left.kind] - KIND_RANK[right.kind] ||
      left.rank - right.rank ||
      left.sku.localeCompare(right.sku),
  );

export const STATUS_LABEL: Readonly<Record<EntitlementStatus, Message>> = {
  active: m.plan_status_active,
  grace: m.plan_status_grace,
  canceled: m.plan_status_canceled,
  revoked: m.plan_status_revoked,
};

export type StatusTone = "good" | "warning" | "serious" | "critical";

export const STATUS_TONE: Readonly<Record<EntitlementStatus, StatusTone>> = {
  active: "good",
  grace: "warning",
  canceled: "serious",
  revoked: "critical",
};

interface SkuCopy {
  readonly title: Message;
  readonly hint: Message;
}

const SKU_COPY: Readonly<Record<string, SkuCopy>> = {
  [FREE_SKU]: { title: m.plan_sku_free, hint: m.plan_sku_free_hint },
  [TIER_PRO_SKU]: { title: m.plan_sku_pro, hint: m.plan_sku_pro_hint },
  [TITAN_S_SKU]: { title: m.plan_sku_titan, hint: m.plan_sku_titan_hint },
  [TITAN_M_SKU]: { title: m.plan_sku_titan, hint: m.plan_sku_titan_hint },
  [TITAN_L_SKU]: { title: m.plan_sku_titan, hint: m.plan_sku_titan_hint },
};

const skuCopy = (sku: string): SkuCopy | null => SKU_COPY[sku] ?? null;

export const copyOf = (sku: PlanSku): SkuCopy | null => skuCopy(sku.sku);

export const skuName = (sku: string): string => {
  const copy = skuCopy(sku);
  return copy === null ? sku : copy.title();
};

export const skuHint = (sku: string): string | null => {
  const copy = skuCopy(sku);
  return copy === null ? null : copy.hint();
};

const DAY_MS = 86_400_000;

export const hourDays = (hoursFrom: string | null, plan: Entitlement): number =>
  hoursFrom === null
    ? hourlyDays(plan)
    : Math.max(0, Math.floor((Date.now() - Date.parse(hoursFrom)) / DAY_MS));
